import {
  AddIconData,
  HomeIconData,
  OpenInNewIconData,
  PrintIconData,
} from "@meiden-connect/material-symbols";
import { Button, Icon, useShowToast } from "@meiden-connect/ui";
import { useEffect, useMemo, useState } from "react";
import { generateCanvasId } from "../../../lib/canvas-id";
import { DEFAULT_CANVAS_TITLE, normalizeCanvasTitle } from "../../../lib/canvas-title";
import {
  openPrintWindowWithImage,
  renderCanvasExportImage,
} from "../../../lib/canvas-pdf-export";
import { buildPoBoardEpicUrl } from "../../../lib/po-board-link";
import { getWebConfig } from "../../../env";
import { navigate } from "../../../router";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly canvasId: string;
}

// 左上のフローティングアプリメニュー。
// TOP / 新規ボタンに加えて、現在のキャンバス名をその場で編集できるようにする。
// canvasId が PO ボードのエピック ID（`epic-{UUIDv4}` 形式）と一致する場合は、
// 紐付くエピックを別タブで開くボタンも併せて表示する。
export function AppMenu({ canvasId }: Props): JSX.Element {
  const rawTitle = useCanvasDocStore((s) => s.meta.title);
  // 旧データ「未保存のキャンバス」も含めて表示時には常に正規化する。
  const title = normalizeCanvasTitle(rawTitle);
  const dispatcher = useCanvasOpDispatcherContext();
  const [draftTitle, setDraftTitle] = useState(title);
  // PDF 出力ボタンの連打防止用のビジー状態。描画と新規ウィンドウ起動が完了するまで再押下を抑止する。
  const [pdfExportBusy, setPdfExportBusy] = useState(false);
  const showToast = useShowToast();
  // PO ボードのエピック詳細を別タブで開くための URL。
  // 環境変数 `VITE_PO_BOARD_ORIGIN` が設定されていればそれを優先し、
  // 未設定の場合はランタイム設定の serviceOrigin（あるいは現在のホスト）の
  // `refinement.` を `po-board.` に置換して導出する。
  // CloudFront 既定ドメイン等で window.location.origin が正規 serviceOrigin と
  // 一致しない経路でもボタンを正しく表示するため、ランタイム設定を優先する。
  const poBoardEpicUrl = useMemo(() => {
    const config = getWebConfig();
    const baseOrigin =
      config.serviceOrigin ??
      (typeof window !== "undefined" ? window.location.origin : undefined);
    return buildPoBoardEpicUrl(
      canvasId,
      baseOrigin,
      import.meta.env.VITE_PO_BOARD_ORIGIN as string | undefined,
    );
  }, [canvasId]);

  useEffect(() => {
    setDraftTitle(title);
  }, [title]);

  const commitTitle = (): void => {
    const trimmed = draftTitle.trim();
    const next = trimmed.length > 0 ? trimmed : DEFAULT_CANVAS_TITLE;
    setDraftTitle(next);
    if (next !== rawTitle) {
      dispatcher.updateCanvasMeta({ title: next });
    }
  };

  // 現在のキャンバスを高解像度 PNG に描画したうえで新規ウィンドウに表示し、印刷ダイアログを開く。
  // 連打防止のため exportBusy 中は再実行できない。
  const exportPdf = async (): Promise<void> => {
    if (pdfExportBusy) return;
    // 画像生成（最大数秒）を待ってから window.open() すると user activation が切れ、
    // ポップアップブロックで null になりやすい。クリック直後の同期処理で先に空ウィンドウを開き、
    // 開けなかった場合はその場でトースト通知する（無通知失敗を防ぐ）。
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      showToast(
        "warning",
        "PDF 出力用のウィンドウを開けませんでした。ブラウザのポップアップ設定をご確認ください。"
      );
      return;
    }
    setPdfExportBusy(true);
    try {
      const objects = Object.values(useCanvasDocStore.getState().objects);
      const dataUrl = await renderCanvasExportImage(objects);
      if (!dataUrl) {
        printWindow.close();
        showToast("warning", "PDF 出力用の画像生成に失敗しました。再度お試しください。");
        return;
      }
      // 既に開いている空ウィンドウへ HTML を書き込む。
      openPrintWindowWithImage(printWindow, dataUrl, `${title} - PDF`);
    } catch (error) {
      printWindow.close();
      console.warn("[pdf-export] PDF 出力に失敗", error);
      showToast("warning", "PDF 出力に失敗しました。再度お試しください。");
    } finally {
      setPdfExportBusy(false);
    }
  };

  return (
    <div className="canvas-app-menu">
      <Button
        type="button"
        variant="action"
        onClick={() => navigate("/")}
        aria-label="ダッシュボードへ戻る"
        title="ダッシュボードへ戻る"
      >
        <Icon d={HomeIconData} />
      </Button>
      <Button
        type="button"
        variant="action"
        // 新規キャンバスは現在のキャンバスと比較・参照しながら使えるよう別ウィンドウで開く。
        onClick={() => window.open(`/c/${generateCanvasId()}`, "_blank", "noopener,noreferrer")}
        aria-label="新規キャンバスを別ウィンドウで作成"
        title="新規キャンバスを別ウィンドウで作成"
      >
        <Icon d={AddIconData} />
      </Button>
      {poBoardEpicUrl !== null ? (
        <Button
          type="button"
          variant="action"
          // PO ボード側のエピック詳細を別タブで開く。
          onClick={() =>
            window.open(poBoardEpicUrl, "_blank", "noopener,noreferrer")
          }
          aria-label="PO ボードのエピックを開く"
          title="PO ボードのエピックを開く"
        >
          <Icon d={OpenInNewIconData} />
        </Button>
      ) : null}
      <label className="canvas-title-editor">
        <span>キャンバス名</span>
        <input
          aria-label="キャンバス名"
          value={draftTitle}
          onChange={(event) => setDraftTitle(event.currentTarget.value)}
          onBlur={commitTitle}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
        />
      </label>
      <Button
        type="button"
        variant="action"
        // 連打防止のため、出力完了までボタンを無効化しビジー表示にする。
        // 本ボタンは印刷ダイアログを開く（PDF 保存も含む）汎用の印刷導線として扱う。
        disabled={pdfExportBusy}
        aria-busy={pdfExportBusy}
        aria-label={pdfExportBusy ? "印刷準備中" : "キャンバスを印刷 / PDF として出力"}
        title={pdfExportBusy ? "印刷準備中" : "キャンバスを印刷 / PDF として出力"}
        onClick={() => {
          void exportPdf();
        }}
      >
        <Icon d={PrintIconData} />
      </Button>
    </div>
  );
}
